/**
 * Назначение и замена транспорта и водителя — блок `WEB-2.2`, требования 407,
 * 408, 409 и 410.
 *
 * Экран читает работу (`getWork`) один раз на загрузку, машины фирмы из адреса
 * **с `work_id` этой работы** (`listCompanyVehicles`) и водителей фирмы
 * (`listCompanyDrivers`). Тягачи и прицепы раскладываются по виду машины
 * перебором, в порядке ответа; прицеп можно не брать — «без прицепа» стоит
 * первым и уходит `trailer_id: null` (решение формы Б13). Машина, которую
 * guard не пропустит, видна, но выбрать её нельзя, а рядом — причина подписью
 * (`assignable` и `unassignable_reason` считает сервер; значение вне перечня —
 * «неизвестно», CR-007). Состояния ремонта и вывода экран ради выбора не
 * читает, своей занятости не считает: `work_id` делает машину этой работы
 * назначаемой на стороне сервера.
 *
 * Водитель — запись списка водителей по имени (решение формы Б14); отказ этого
 * списка встаёт текстом сервера на месте выбора водителя, а машины, привязка и
 * история остаются. Условий допуска — прав, телефона, вида машины, занятости —
 * клиент не проверяет: тело `assignWork` ровно из трёх полей уходит на сервер,
 * и его `422` ложится пачкой, каждое сообщение под своим полем разом.
 *
 * Замена — та же форма, заполненная текущей привязкой; над ней — текущая
 * привязка, под ней — история прежних привязок из ответа, в его порядке
 * (решение формы Б15): текущую клиент в историю не дописывает. Форма и кнопка
 * есть ровно при действии сервера — элементе `available_actions` со статусом
 * назначения; без него — привязка и история, а на месте формы нейтральная
 * строка без причины (решение формы Б16, переназначение при неявке — пока
 * сервер даёт действие). Ответ `200` — экран рисуется из ответа и перечитывает
 * машины; отказ сервера — его текст, работа и машины перечитаны, выбор цел.
 *
 * Смена языка не стирает выбора (долг `TD-72`): экран переподписывается.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import type {
  CompanyDriver,
  ErrorDetail,
  FleetVehicle,
  Vehicle,
  Work,
  WorkAssignmentInput,
  WorkAssignmentRecord,
} from '../api/contract';
import { activeLocale, unassignableReasonStringKey, vehicleKindStringKey } from '../i18n';
import type { Strings } from '../i18n';
import { worksPath } from '../router';
import {
  createFieldMessageSlot,
  enumLabel,
  momentTimeLabel,
  placeState,
  refusalText,
  renderFieldMessages,
  screenStateFor,
} from './state';
import type { ScreenState } from './state';

export interface WorkAssignmentScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
  readonly workId: string;
}

export interface WorkAssignmentScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  load(): Promise<void>;
  /** Подписи на новом языке; выбор остаётся на месте (долг `TD-72`). */
  relabel(next: Strings): void;
}

/**
 * Статус действия, которым сервер разрешает назначение и замену, — значение
 * названо в файле один раз. Оно решает не «можно ли», а «показывать ли форму»:
 * назначение судит guard сервера, и форма, открытая до приёма, получит `409`.
 */
const ASSIGN_ACTION: Work['status'] = 'ASSIGNED';

/** Виды машин для раскладки выбора — значения контракта, не правило допуска. */
const TRACTOR_KIND: FleetVehicle['kind'] = 'tractor';
const TRAILER_KIND: FleetVehicle['kind'] = 'trailer';

/** Привязка — текущая (`Work.assignment`) либо прежняя (запись истории). */
type Binding = NonNullable<Work['assignment']> | WorkAssignmentRecord;

/**
 * Детали отказа, сведённые по полю: несколько сообщений об одном поле — одной
 * строкой под ним. Механизм показа один (`renderFieldMessages`) и кладёт под
 * поле одно сообщение; пачка guard'а называет одно поле дважды (права и ADR
 * водителя), и ни одно из сообщений не теряется.
 */
function mergedByField(details: readonly ErrorDetail[]): ErrorDetail[] {
  const merged: ErrorDetail[] = [];

  for (const detail of details) {
    let joined = false;

    for (const [index, seen] of merged.entries()) {
      if (seen.field === detail.field) {
        merged[index] = { ...seen, message: `${seen.message} ${detail.message}` };
        joined = true;

        break;
      }
    }

    if (!joined) {
      merged.push(detail);
    }
  }

  return merged;
}

export function createWorkAssignmentScreen(options: WorkAssignmentScreenOptions): WorkAssignmentScreen {
  const { client, companyId, workId } = options;
  let strings = options.strings;

  const element = document.createElement('section');
  const backLink = document.createElement('a');
  const heading = document.createElement('h1');
  const subtitle = document.createElement('p');
  const current = document.createElement('p');
  const notice = document.createElement('p');
  const formArea = document.createElement('div');
  const form = document.createElement('form');
  const historyHeading = document.createElement('h2');
  const historyList = document.createElement('ul');
  const historyEmpty = document.createElement('p');
  const unavailable = document.createElement('p');

  element.className = 'screen screen-work-assignment';
  backLink.className = 'company-link';
  backLink.href = worksPath(companyId);
  current.className = 'work-assignment-current';
  notice.className = 'notice';
  notice.setAttribute('role', 'alert');
  form.className = 'form';
  form.noValidate = true;

  /** Подписи экрана: каждая ставится одной записью — и при сборке, и при смене языка. */
  const captions: Array<() => void> = [];

  function caption(apply: () => void): void {
    captions.push(apply);
    apply();
  }

  function labelled(control: HTMLElement, text: () => string): HTMLLabelElement {
    const label = document.createElement('label');
    const shown = document.createElement('span');

    label.className = 'form-field';
    shown.className = 'form-field__caption';
    caption(() => {
      shown.textContent = text();
    });
    label.append(shown, control, createFieldMessageSlot());

    return label;
  }

  function select(name: string): HTMLSelectElement {
    const node = document.createElement('select');

    node.name = name;

    return node;
  }

  const tractorSelect = select('tractor_id');
  const trailerSelect = select('trailer_id');
  const driverSelect = select('driver_member_id');
  const driversRefusal = document.createElement('span');
  /** Место выбора водителя: выбор либо текст отказа списка водителей. */
  const driverPlace = document.createElement('span');
  const submitButton = document.createElement('button');
  const driverLabel = labelled(driverPlace, () => strings.work_assignment_driver);

  submitButton.type = 'submit';
  submitButton.className = 'action';
  driversRefusal.className = 'notice';

  form.append(
    labelled(tractorSelect, () => strings.work_assignment_tractor),
    labelled(trailerSelect, () => strings.work_assignment_trailer),
    driverLabel,
    submitButton,
  );

  caption(() => {
    heading.textContent = strings.work_assignment_title;
    backLink.textContent = strings.works_title;
    historyHeading.textContent = strings.work_assignment_history;
    historyEmpty.textContent = strings.work_assignment_history_empty;
    unavailable.textContent = strings.work_assignment_unavailable;
  });

  let state: ScreenState | null = 'loading';
  /** Пришёл ли ответ сервера — величина правила места состояния. */
  let answered = false;
  let work: Work | null = null;
  let vehicles: readonly FleetVehicle[] = [];
  let drivers: readonly CompanyDriver[] = [];
  /** Текст отказа списка водителей либо `null` — список пришёл. */
  let driversFailure: string | null = null;
  /** Назначение в пути: повтор отправки до ответа второго запроса не шлёт. */
  let sending = false;

  /** Подпись машины: вид и VIN, у недоступной — причина словами сервера. */
  function vehicleCaption(vehicle: FleetVehicle): string {
    const shown = `${enumLabel(strings, vehicleKindStringKey(vehicle.kind))} ${vehicle.vin}`;

    if (vehicle.assignable || vehicle.unassignable_reason === null) {
      return shown;
    }

    return `${shown} — ${enumLabel(strings, unassignableReasonStringKey(vehicle.unassignable_reason))}`;
  }

  function option(value: string, text: string): HTMLOptionElement {
    const node = document.createElement('option');

    node.value = value;
    node.textContent = text;

    return node;
  }

  /** Варианты выбора заменяются, выбранное значение остаётся на месте. */
  function refill(target: HTMLSelectElement, choices: readonly HTMLOptionElement[]): void {
    const chosen = target.value;

    target.replaceChildren(...choices);
    target.value = chosen;

    if (target.value !== chosen) {
      target.value = '';
    }
  }

  function fillVehicles(): void {
    const tractors: HTMLOptionElement[] = [option('', '')];
    const trailers: HTMLOptionElement[] = [option('', strings.work_assignment_no_trailer)];

    // Раскладка по виду — перебором, в порядке ответа; недоступная машина
    // видна, выбрать её нельзя — решает признак сервера, а не состояние машины.
    for (const vehicle of vehicles) {
      const choice = option(vehicle.id, vehicleCaption(vehicle));

      choice.disabled = !vehicle.assignable;

      if (vehicle.kind === TRACTOR_KIND) {
        tractors.push(choice);
      } else if (vehicle.kind === TRAILER_KIND) {
        trailers.push(choice);
      }
    }

    refill(tractorSelect, tractors);
    refill(trailerSelect, trailers);
  }

  function fillDrivers(): void {
    const choices: HTMLOptionElement[] = [option('', '')];
    const listed = new Set<string>();

    for (const driver of drivers) {
      choices.push(option(driver.membership_id, driver.name ?? strings.work_assignment_unnamed));
      listed.add(driver.membership_id);
    }

    // Водитель текущей привязки остаётся вариантом и тогда, когда списка нет
    // (отказ списка водителей): замена машин с прежним водителем не теряет его.
    const assigned = work?.assignment?.driver ?? null;

    if (assigned !== null && !listed.has(assigned.member_id)) {
      choices.push(option(assigned.member_id, assigned.name ?? strings.work_assignment_unnamed));
    }

    refill(driverSelect, choices);
    driversRefusal.textContent = driversFailure ?? '';
    driverPlace.replaceChildren(driversFailure === null ? driverSelect : driversRefusal);
  }

  /** Форма заполняется привязкой работы — при загрузке и после ответа `200`. */
  function prefill(shown: Work): void {
    if (shown.assignment === null) {
      return;
    }

    const { tractor, trailer, driver } = shown.assignment;

    tractorSelect.value = tractor.id;
    trailerSelect.value = trailer === null ? '' : trailer.id;
    driverSelect.value = driver.member_id;
  }

  function vehicleShown(vehicle: Vehicle): string {
    return `${enumLabel(strings, vehicleKindStringKey(vehicle.kind))} ${vehicle.vin}`;
  }

  /** Машины и водитель привязки подписью: прицепа нет — «без прицепа». */
  function bindingParts({ tractor, trailer, driver }: Binding): string[] {
    return [
      vehicleShown(tractor),
      trailer === null ? strings.work_assignment_no_trailer : vehicleShown(trailer),
      driver.name ?? strings.work_assignment_unnamed,
    ];
  }

  function historyRow(record: WorkAssignmentRecord): HTMLElement {
    const node = document.createElement('li');
    const locale = activeLocale();

    node.className = 'work-assignment-history-row';
    node.textContent = [
      ...bindingParts(record),
      `${strings.work_assignment_assigned_by}: ${record.assigned_by.name ?? strings.work_assignment_unnamed} ${momentTimeLabel(locale, record.assigned_at)}`,
      `${strings.work_assignment_replaced_by}: ${record.replaced_by.name ?? strings.work_assignment_unnamed} ${momentTimeLabel(locale, record.replaced_at)}`,
    ].join(' · ');

    return node;
  }

  /** Есть ли у работы действие назначения, отданное сервером. */
  function offersAssignment(shown: Work): boolean {
    for (const action of shown.available_actions) {
      if (action.status === ASSIGN_ACTION) {
        return true;
      }
    }

    return false;
  }

  function render(): void {
    const frame: HTMLElement[] = [backLink];
    const after: HTMLElement[] = [];

    if (work !== null) {
      const assignment = work.assignment;

      subtitle.textContent = work.title;
      frame.push(heading, subtitle);

      if (assignment !== null) {
        const { driver } = assignment;

        current.textContent = [
          strings.work_assignment_current,
          ...bindingParts(assignment),
          driver.phone,
          `${strings.work_assignment_assigned_at}: ${momentTimeLabel(activeLocale(), assignment.assigned_at)}`,
        ].join(' · ');
        frame.push(current);
      }

      frame.push(notice);
      fillVehicles();
      fillDrivers();
      submitButton.textContent = assignment === null ? strings.work_assignment_assign : strings.work_assignment_replace;
      formArea.replaceChildren(offersAssignment(work) ? form : unavailable);

      const rows: HTMLElement[] = [];

      for (const record of work.assignment_history) {
        rows.push(historyRow(record));
      }

      historyList.replaceChildren(...rows);
      after.push(historyHeading, rows.length === 0 ? historyEmpty : historyList);
    }

    placeState(element, { state, answered, frame, list: formArea, after }, strings);
  }

  /** Список водителей: его отказ — текстом сервера на месте выбора водителя. */
  async function readDrivers(): Promise<void> {
    try {
      drivers = await client.listCompanyDrivers(companyId);
      driversFailure = null;
    } catch (error) {
      drivers = [];
      driversFailure = refusalText(error, strings);
    }
  }

  async function readVehicles(): Promise<void> {
    vehicles = await client.listCompanyVehicles(companyId, { work_id: workId });
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    answered = false;
    work = null;
    notice.textContent = '';
    render();

    const driversRead = readDrivers();

    try {
      const [received] = await Promise.all([client.getWork(workId), readVehicles()]);

      await driversRead;
      work = received;
      answered = true;
      state = screenStateFor({ kind: 'done', empty: false });
      render();
      prefill(received);
    } catch (error) {
      await driversRead;
      work = null;
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  /** Тело — ровно три поля `WorkAssignmentInput`; «без прицепа» — `null`. */
  function entered(): WorkAssignmentInput {
    return {
      tractor_id: tractorSelect.value,
      trailer_id: trailerSelect.value === '' ? null : trailerSelect.value,
      driver_member_id: driverSelect.value,
    };
  }

  async function send(): Promise<void> {
    sending = true;
    submitButton.disabled = true;
    notice.textContent = '';
    renderFieldMessages(form, []);

    try {
      // Экран рисуется из ответа: привязка и история — его, а не дописанные.
      const saved = await client.assignWork(workId, entered());

      work = saved;
      render();
      prefill(saved);

      try {
        await readVehicles();
      } catch (error) {
        notice.textContent = refusalText(error, strings);
      }
    } catch (error) {
      const failure = error instanceof ApiFailure ? error : null;
      const homeless = renderFieldMessages(form, mergedByField(failure?.details ?? []));
      const texts: string[] = [];

      for (const detail of homeless) {
        texts.push(detail.message);
      }

      if (texts.length === 0) {
        texts.push(refusalText(error, strings));
      }

      notice.textContent = texts.join(' ');

      // Сервер ответил отказом — работу могли принять, машину занять: работа и
      // машины перечитываются, выбор остаётся. Обрыв связи ответа не дал.
      if (failure !== null && failure.code !== null) {
        try {
          const [received] = await Promise.all([client.getWork(workId), readVehicles()]);

          work = received;
        } catch (reread) {
          notice.textContent = `${notice.textContent} ${refusalText(reread, strings)}`;
        }
      }
    } finally {
      sending = false;
      submitButton.disabled = false;
    }

    render();
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    if (sending) {
      return;
    }

    void send();
  });

  function relabel(next: Strings): void {
    strings = next;

    for (const apply of captions) {
      apply();
    }

    render();
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    load,
    relabel,
  };
}
